import { Modal } from '@/components';
import { userLevelToKr } from '@/functions/user-level-to-kr';
import type { Site, User } from '@/types/site';
import { useState } from 'react';

interface SiteManagerModalProps {
  isOpen: boolean;
  site: Site | null;
  currentManagers: User[];
  allManagers: User[];
  onClose: () => void;
  onAddManager: (managerId: string) => void;
  onRemoveManager: (managerId: string) => void;
}

export default function SiteManagerModal({
  isOpen,
  site,
  currentManagers,
  allManagers,
  onClose,
  onAddManager,
  onRemoveManager,
}: SiteManagerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');

  if (!site) return null;

  const availableManagers = allManagers.filter(
    (manager) => !currentManagers.some((m) => m.id === manager.id)
  );

  // 검색어로 필터링
  const filteredManagers = availableManagers.filter((manager) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const name = (manager?.name || '').toLowerCase();
    const email = (manager?.email || '').toLowerCase();
    const userLevel = (
      manager?.userLevel ? userLevelToKr(manager.userLevel) || '' : ''
    ).toLowerCase();

    return (
      name.includes(query) || email.includes(query) || userLevel.includes(query)
    );
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${site.name} - 관리자 설정`}
      size="4xl"
      maxHeight="max-h-[90vh]"
    >
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 현재 관리자 목록 */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-[30px]">
              현재 관리자 목록
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
              {currentManagers.length > 0 ? (
                <div className="space-y-3">
                  {currentManagers.map((manager) => (
                    <div
                      key={manager.id}
                      className="bg-white rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                          <i className="ri-user-line"></i>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {manager.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {userLevelToKr(manager.userLevel)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {manager.email || '-'}{' '}
                            {manager.mobilePhone && `/ ${manager.mobilePhone}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveManager(manager.id)}
                        className="text-red-600 hover:text-red-800 cursor-pointer"
                        title="관리자 제거"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-close-circle-line"></i>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-3">
                    <i className="ri-user-line text-gray-400"></i>
                  </div>
                  <p>지정된 관리자가 없습니다</p>
                </div>
              )}
            </div>
          </div>

          {/* 관리자 추가 목록 */}
          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                관리자 추가
              </h4>

              {/* 검색바 */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="이름, 이메일, 권한으로 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute left-3 top-2.5 text-gray-400">
                    <i className="ri-search-line"></i>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
              <div className="space-y-3">
                {filteredManagers.length > 0 ? (
                  filteredManagers.map((manager) => (
                    <div
                      key={manager.id}
                      className="bg-white rounded-lg p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                          <i className="ri-user-line text-gray-600"></i>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {manager.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {userLevelToKr(manager.userLevel)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {manager.email || '-'}{' '}
                            {manager.mobilePhone && `/ ${manager.mobilePhone}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onAddManager(manager.id)}
                        className="text-green-600 hover:text-green-800 cursor-pointer"
                        title="관리자 추가"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-add-circle-line"></i>
                        </div>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-3">
                      <i
                        className={`${searchQuery ? 'ri-search-line' : 'ri-user-add-line'} text-gray-400`}
                      ></i>
                    </div>
                    <p>
                      {searchQuery
                        ? `"${searchQuery}"에 대한 검색 결과가 없습니다`
                        : '추가할 수 있는 관리자가 없습니다'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg cursor-pointer whitespace-nowrap"
          >
            완료
          </button>
        </div>
      </div>
    </Modal>
  );
}
